import { useState } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import styled, { css } from 'styled-components'
import {
    DEFAULT_FOLDER_ID,
    DONE_FOLDER_ID,
    DONE_FOLDER_NAME,
    TASK_INBOX_NAME,
    TRASH_FOLDER_ID,
    TRASH_FOLDER_NAME,
} from '../../constants'
import KEYBOARD_SHORTCUTS from '../../constants/shortcuts'
import { usePreviewMode } from '../../hooks'
import { useGetFolders } from '../../services/api/folders.hooks'
import Log from '../../services/api/log'
import { useGetUserInfo } from '../../services/api/user-info.hooks'
import { Border, Colors, Spacing, Typography } from '../../styles'
import { icons } from '../../styles/images'
import { Icon } from '../atoms/Icon'
import { useCalendarContext } from '../calendar/CalendarContext'
import CommandPalette from '../molecules/CommandPalette'
import SettingsModalButton from '../molecules/SettingsModalButton'
import ShareFeedbackButton from '../molecules/ShareFeedbackButton'
import TrialIndicator from '../molecules/TrialIndicator'
import IntegrationLinks from '../navigation_sidebar/IntegrationLinks'
import NavigationLink, { CollapsedIconContainer, CollapsedNavButton } from '../navigation_sidebar/NavigationLink'
import ShellLogo from '../navigation_sidebar/ShellLogo'
import NoteCreateButton from '../notes/NoteCreateButton'
import GTDropdownMenu from '../radix/GTDropdownMenu'
import { GTMenuItem } from '../radix/RadixUIConstants'
import Tip from '../radix/Tip'

const PositionedLogo = styled(ShellLogo)`
    margin-bottom: ${Spacing._32};
`
const CollapseAndCommandPaletteContainer = styled.div`
    display: flex;
    flex-direction: column;
    margin-bottom: ${Spacing._32};
    gap: ${Spacing._8};
`
const CollapsedContainer = styled.div`
    padding: ${Spacing._24} 0;
    display: flex;
    flex-direction: column;
    height: 100%;
    align-items: center;
    box-sizing: border-box;
`
const FoldersContainer = styled.div<{ isCollapsed?: boolean }>`
    margin-top: ${Spacing._32};
    display: flex;
    flex-direction: column;
    ${({ isCollapsed }) => isCollapsed && `gap: ${Spacing._8};`}
`
const PrimaryLinksContainer = styled.div`
    display: flex;
    flex-direction: column;
    gap: ${Spacing._8};
`
const UpperContainer = styled.div`
    display: flex;
    flex-direction: column;
    align-items: center;
`
const MiddleContainer = styled.div`
    overflow-y: auto;
    flex: 1;
    @supports (overflow-y: overlay) {
        overflow-y: overlay;
    }
`
const LowerContainer = styled.div`
    margin: auto 0;
    padding-top: ${Spacing._8};
    display: flex;
    flex-direction: column;
    gap: ${Spacing._8};
`
const NOT_DROPDOWN_MENU_ITEM_IDs = [DEFAULT_FOLDER_ID, DONE_FOLDER_ID, TRASH_FOLDER_ID]
const Beta = styled.div<{ isPreviewMode: boolean }>`
    display: flex;
    align-items: center;
    justify-content: center;
    text-align: center;
    margin: 0 auto;
    background-color: ${Colors.background.sub};
    border: ${Border.stroke.medium} solid ${Colors.background.border};
    color: ${Colors.text.base};
    user-select: none;
    cursor: pointer;
    border-radius: ${Border.radius.medium};
    aspect-ratio: 1/1;
    width: ${Spacing._40};
    ${Typography.label.medium};
    ${(props) =>
        props.isPreviewMode &&
        css`
            background-color: ${Colors.control.primary.bg};
            border-color: ${Colors.control.primary.stroke};
            color: ${Colors.control.primary.label};
        `}
`

interface NavigationViewCollapsedProps {
    setIsCollapsed: (isCollapsed: boolean) => void
}
const NavigationViewCollapsed = ({ setIsCollapsed }: NavigationViewCollapsedProps) => {
    const { data: folders } = useGetFolders()
    const { section: sectionId } = useParams()
    const { showTaskToCalSidebar, setShowTaskToCalSidebar, calendarType } = useCalendarContext()
    const [isDropdownOpen, setIsDropdownOpen] = useState(false)
    const { isPreviewMode, toggle: togglePreviewMode } = usePreviewMode()
    const navigate = useNavigate()

    const DEFAULT_FOLDER = folders?.find((folder) => folder.id === DEFAULT_FOLDER_ID)
    const TRASH_FOLDER = folders?.find((folder) => folder.id === TRASH_FOLDER_ID)
    const DONE_FOLDER = folders?.find((folder) => folder.id === DONE_FOLDER_ID)

    const filteredFolders = folders?.filter((folder) => !NOT_DROPDOWN_MENU_ITEM_IDs.includes(folder.id)) ?? []
    const filteredFoldersIds = filteredFolders?.map((folder) => folder.id) ?? []

    const items: GTMenuItem[] =
        filteredFolders?.map((folder) => ({
            label: `${folder.name} (${folder.task_ids.length})`,
            onClick: () => {
                if (!showTaskToCalSidebar && calendarType === 'week') {
                    setShowTaskToCalSidebar(true)
                }
                Log(`navigate__/tasks/${folder.id}`)
                navigate(`/tasks/${folder.id}`)
            },
            icon: icons.folder,
        })) ?? []
    const { data: userInfo } = useGetUserInfo()

    return (
        <CollapsedContainer>
            <UpperContainer>
                <PositionedLogo size={32} />
                <CollapseAndCommandPaletteContainer>
                    <Tip shortcutName="navigationView" side="right">
                        <CollapsedNavButton
                            type="button"
                            aria-label={KEYBOARD_SHORTCUTS.navigationView.label}
                            onClick={() => setIsCollapsed(false)}
                        >
                            <CollapsedIconContainer>
                                <Icon icon={icons.sidebar} />
                            </CollapsedIconContainer>
                        </CollapsedNavButton>
                    </Tip>
                    <NoteCreateButton type="collapsed" />
                    <CommandPalette
                        customButton={
                            <Tip shortcutName="toggleCommandPalette" side="right">
                                {/* CommandPalette opens on the click this button bubbles up. */}
                                <CollapsedNavButton
                                    type="button"
                                    aria-label={KEYBOARD_SHORTCUTS.toggleCommandPalette.label}
                                >
                                    <CollapsedIconContainer>
                                        <Icon icon={icons.magnifying_glass} />
                                    </CollapsedIconContainer>
                                </CollapsedNavButton>
                            </Tip>
                        }
                    />
                </CollapseAndCommandPaletteContainer>
            </UpperContainer>
            <MiddleContainer>
                <PrimaryLinksContainer>
                    <IntegrationLinks isCollapsed />
                </PrimaryLinksContainer>
                <FoldersContainer isCollapsed>
                    {DEFAULT_FOLDER && (
                        <Tip shortcutName="goToTaskInbox" side="right">
                            <NavigationLink
                                link={`/tasks/${DEFAULT_FOLDER_ID}`}
                                title={TASK_INBOX_NAME}
                                icon={icons.inbox}
                                isCurrentPage={sectionId === DEFAULT_FOLDER_ID}
                                taskFolder={DEFAULT_FOLDER}
                                count={DEFAULT_FOLDER.task_ids.length}
                                isCollapsed
                                droppable
                            />
                        </Tip>
                    )}
                    {items.length > 0 && (
                        <GTDropdownMenu
                            items={items}
                            isOpen={isDropdownOpen}
                            side="right"
                            setIsOpen={setIsDropdownOpen}
                            hideCheckmark
                            trigger={
                                <CollapsedIconContainer isSelected={filteredFoldersIds.includes(sectionId || '')}>
                                    <Icon icon={icons.folder} />
                                </CollapsedIconContainer>
                            }
                        />
                    )}
                    {DONE_FOLDER && (
                        <NavigationLink
                            link={`/tasks/${DONE_FOLDER_ID}`}
                            title={DONE_FOLDER_NAME}
                            icon={icons.checkbox_checked}
                            isCurrentPage={sectionId === DONE_FOLDER_ID}
                            taskFolder={DONE_FOLDER}
                            count={DONE_FOLDER.task_ids.length}
                            isCollapsed
                            droppable
                        />
                    )}
                    {TRASH_FOLDER && (
                        <NavigationLink
                            link={`/tasks/${TRASH_FOLDER_ID}`}
                            title={TRASH_FOLDER_NAME}
                            icon={icons.trash}
                            isCurrentPage={sectionId === TRASH_FOLDER_ID}
                            taskFolder={TRASH_FOLDER}
                            count={TRASH_FOLDER.task_ids.length}
                            isCollapsed
                            droppable
                        />
                    )}
                </FoldersContainer>
            </MiddleContainer>
            <LowerContainer>
                <TrialIndicator isCollapsed />
                <ShareFeedbackButton isCollapsed />
                <SettingsModalButton type="collapsed-nav-button" />
                {userInfo?.is_employee && (
                    <Beta isPreviewMode={isPreviewMode} onClick={() => togglePreviewMode()}>
                        {isPreviewMode ? 'GK' : 'GT'}
                    </Beta>
                )}
            </LowerContainer>
        </CollapsedContainer>
    )
}

export default NavigationViewCollapsed
